import { useCallback, useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { ColumnFilterOption } from '@elementary/common-ui/components/Table/types';
import {
  IncidentV2SortField,
  SortOrder,
} from '@elementary/common-ui/services/gql/types';
import { UrlFilters } from '@elementary/common-ui/hooks/useUrlFilters';
import {
  INCIDENT_LIST_FILTER_OPTIONS,
  INCIDENT_LIST_GROUP_SUMMARY,
  INCIDENT_LIST_PAGED,
} from '@elementary/common-ui/constants/queryKeys';
import { INCIDENT_LIST_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import {
  getGroupingStrategyDisplay,
  getIncidentFieldOptions,
  getIncidentGroupValues,
  statusIconMap,
} from 'incidents/utils';
import useGroupByParam from 'shared/hooks/useGroupByParam';
import useGroupedTableData from 'shared/hooks/useGroupedTableData';
import {
  ASSIGNEE_COLUMN_ID,
  COLUMN_TO_INCIDENT_FIELD_MAP,
  GROUPING_STRATEGY_COLUMN_ID,
  NO_ASSIGNEE_VALUE,
  RESOLVED_STATUS,
  SEVERITY_COLUMN_ID,
  SEVERITY_DISPLAY,
  STATUS_COLUMN_ID,
} from '../constants';
import {
  getIncidentsFilterOptionsQuery,
  getIncidentsPagedQuery,
  getIncidentsSummaryQuery,
} from '../api';
import type {
  IncidentFilterOptionsGroup,
  IncidentListBaseFilters,
  IncidentListItem,
  IncidentListSummary,
  IncidentsPagedResponse,
} from '../types';
import IncidentGroupSeverityMetadata from '../Table/IncidentGroupSeverityMetadata';

const DEFAULT_GROUP_BY_COLUMN_ID = STATUS_COLUMN_ID;

const mapIncidentRows = (items: IncidentListItem[]) => items;

type Args = {
  search: string;
  urlFilters: UrlFilters | undefined;
  sortVariable:
    | { field: IncidentV2SortField; direction: SortOrder }
    | undefined;
  baseFilters: IncidentListBaseFilters;
  selectedEnvId: string | undefined;
  selectedIdsHash: string;
  filterOpts: IncidentFilterOptionsGroup[] | undefined;
  enabled?: boolean;
};

const useGroupedIncidents = ({
  search,
  urlFilters,
  sortVariable,
  baseFilters,
  selectedEnvId,
  selectedIdsHash,
  filterOpts,
  enabled = true,
}: Args) => {
  const groupableColumnIds = useMemo(
    () =>
      Object.entries(COLUMN_TO_INCIDENT_FIELD_MAP)
        .filter(
          ([columnId, field]) =>
            columnId === DEFAULT_GROUP_BY_COLUMN_ID ||
            getIncidentFieldOptions(filterOpts, field).length > 0,
        )
        .map(([columnId]) => columnId),
    [filterOpts],
  );

  const { groupBy, onGroupByChange } = useGroupByParam({
    paramKey: INCIDENT_LIST_GROUP_BY,
    defaultGroupBy: DEFAULT_GROUP_BY_COLUMN_ID,
    groupableColumnIds,
  });
  const groupByColumnId = groupBy ?? DEFAULT_GROUP_BY_COLUMN_ID;

  const field = COLUMN_TO_INCIDENT_FIELD_MAP[groupByColumnId];
  const isGroupedByStatus = groupByColumnId === DEFAULT_GROUP_BY_COLUMN_ID;

  const { data: statusSummaryData, isFetching: isStatusSummaryFetching } =
    useGraphqlQuery<{ incidentsV2Summary: IncidentListSummary }>({
      queryKey: [
        INCIDENT_LIST_GROUP_SUMMARY,
        selectedEnvId,
        'status-counts',
        baseFilters,
        selectedIdsHash,
        urlFilters,
      ],
      query: getIncidentsSummaryQuery,
      variables: {
        filters: baseFilters,
        incidentFilters: urlFilters || [],
      },
      enabled: enabled && isGroupedByStatus && !!selectedEnvId,
      envId: selectedEnvId,
      keepPreviousData: true,
    });

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<{
      incidentsV2FilterOptions: IncidentFilterOptionsGroup[];
    }>({
      queryKey: [
        INCIDENT_LIST_FILTER_OPTIONS,
        selectedEnvId,
        'grouped',
        baseFilters,
        selectedIdsHash,
        urlFilters,
      ],
      query: getIncidentsFilterOptionsQuery,
      variables: {
        filters: baseFilters,
        incidentFilters: urlFilters || [],
      },
      enabled: enabled && !isGroupedByStatus && !!field && !!selectedEnvId,
      envId: selectedEnvId,
      keepPreviousData: true,
    });

  const groupValues = useMemo<ColumnFilterOption[]>(
    () =>
      getIncidentGroupValues({
        groupByColumnId,
        groupOptions: groupOptionsData?.incidentsV2FilterOptions,
        statusCounts: statusSummaryData?.incidentsV2Summary.byStatus,
      }),
    [groupByColumnId, groupOptionsData, statusSummaryData],
  );

  const isGroupListFetching = isGroupedByStatus
    ? isStatusSummaryFetching
    : isGroupOptionsFetching;

  const formatGroupTitle = useCallback(
    (value: string) => {
      if (groupByColumnId === SEVERITY_COLUMN_ID) {
        return SEVERITY_DISPLAY[value.toLowerCase()] ?? value;
      }
      if (groupByColumnId === GROUPING_STRATEGY_COLUMN_ID) {
        return getGroupingStrategyDisplay(value);
      }
      if (
        groupByColumnId === ASSIGNEE_COLUMN_ID &&
        value === NO_ASSIGNEE_VALUE
      ) {
        return 'No Assignee';
      }
      return value;
    },
    [groupByColumnId],
  );

  const showSeveritySummary = groupByColumnId !== SEVERITY_COLUMN_ID;

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<IncidentListItem, IncidentsPagedResponse> => ({
      queryKey: [
        INCIDENT_LIST_PAGED,
        selectedEnvId,
        urlFilters,
        baseFilters.daysBack,
        search,
        selectedIdsHash,
        sortVariable,
        field,
        value,
      ],
      query: getIncidentsPagedQuery,
      variables: {
        filters: baseFilters,
        sort: sortVariable,
        limit: 50,
        incidentFilters: field
          ? [
              ...(urlFilters || []),
              { property: field, operator: 'equals', value: [value] },
            ]
          : urlFilters || [],
      },
      select: (data) => data.incidentsV2Paged,
      enabled: enabled && !!field && !!selectedEnvId,
      envId: selectedEnvId,
    }),
    [
      enabled,
      selectedEnvId,
      urlFilters,
      baseFilters,
      search,
      selectedIdsHash,
      sortVariable,
      field,
    ],
  );

  const getGroupDecorations = useCallback(
    (value: string) => {
      const showGroupSeverityMetadata =
        showSeveritySummary &&
        !!field &&
        !(isGroupedByStatus && value === RESOLVED_STATUS);
      return {
        icon: isGroupedByStatus
          ? statusIconMap[value as keyof typeof statusIconMap]
          : undefined,
        metadataRenderer: showGroupSeverityMetadata
          ? () => (
              <IncidentGroupSeverityMetadata
                field={field}
                groupValue={value}
                urlFilters={urlFilters}
                baseFilters={baseFilters}
                selectedEnvId={selectedEnvId}
                selectedIdsHash={selectedIdsHash}
              />
            )
          : undefined,
      };
    },
    [
      showSeveritySummary,
      field,
      isGroupedByStatus,
      urlFilters,
      baseFilters,
      selectedEnvId,
      selectedIdsHash,
    ],
  );

  const {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetching,
    isAnyFetchingWithRows,
    isInitialLoading,
  } = useGroupedTableData({
    groupBy: groupByColumnId,
    groupValues,
    enabled: !!field,
    formatGroupTitle,
    buildQueryOptions,
    mapItems: mapIncidentRows,
    getGroupDecorations,
  });

  const allIncidents = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  const isFetching = isGroupListFetching || isAnyFetching || isInitialLoading;
  const isTableLoading = isGroupListFetching || isAnyFetchingWithRows;

  return {
    groupBy: groupByColumnId,
    onGroupByChange,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    allIncidents,
    isFetching,
    isTableLoading,
    isGroupedByStatus,
  };
};

export default useGroupedIncidents;
